/* ============================================================
   teg — processes.

   Top 25 by memory, as the backend reports them. Sortable and
   filterable, with each row's share drawn against the largest so
   the list reads as a shape, not a column of numbers.
   ============================================================ */

import React, { useMemo, useState } from 'react';
import { Icon, fmtBytes } from './lib';

export default function Processes({ processes }) {
  const [q, setQ] = useState('');
  const [sort, setSort] = useState({ key: 'mem', dir: -1 });

  const rows = useMemo(() => {
    const list = (processes || []).filter((p) =>
      !q || p.name?.toLowerCase().includes(q.toLowerCase()) || String(p.pid).includes(q));
    const cmp = {
      name: (a, b) => (a.name || '').localeCompare(b.name || ''),
      mem: (a, b) => (a.mem || 0) - (b.mem || 0),
      cpu: (a, b) => (a.cpu || 0) - (b.cpu || 0),
      pid: (a, b) => (a.pid || 0) - (b.pid || 0),
      user: (a, b) => (a.user || '').localeCompare(b.user || ''),
    }[sort.key];
    return [...list].sort((a, b) => cmp(a, b) * sort.dir);
  }, [processes, q, sort]);

  const max = Math.max(1, ...(processes || []).map((p) => p.mem || 0));
  const totalMem = (processes || []).reduce((n, p) => n + (p.mem || 0), 0);

  return (
    <div className="tg-view">
      <div className="tg-view-head">
        <h2 className="h2">Processes</h2>
        <span className="meta">top {(processes || []).length} by memory · {fmtBytes(totalMem)} between them</span>
        <label className="tg-search">
          <Icon name="search" size={14}/>
          <input className="input tg-search-input" placeholder="Filter" value={q}
                 onChange={(e) => setQ(e.target.value)} aria-label="Filter processes"/>
        </label>
      </div>

      <div className="panel">
        <div className="tg-proc tg-proc-head">
          {[['name', 'Process'], ['pid', 'PID'], ['user', 'User'], ['cpu', 'CPU'], ['mem', 'Memory']].map(([k, label]) => (
            <button key={k} type="button" className={`tg-sort ${sort.key === k ? 'is-on' : ''}`}
                    onClick={() => setSort((s) => ({ key: k, dir: s.key === k ? -s.dir : -1 }))}>
              {label}{sort.key === k ? (sort.dir > 0 ? ' ↑' : ' ↓') : ''}
            </button>
          ))}
          <span/>
        </div>
        {rows.map((p) => (
          <div key={p.pid} className="tg-proc">
            <span className="tg-proc-name" title={p.name}>{p.name}</span>
            <span className="tnum meta">{p.pid}</span>
            <span className="meta tg-proc-user">{p.user}</span>
            <span className="tnum meta">{p.cpu ? `${p.cpu.toFixed(0)}%` : '—'}</span>
            <span className="tnum tg-proc-mem">{fmtBytes(p.mem)}</span>
            <span className="bar tg-proc-bar tg-hint" tabIndex={0}
                  data-hint={`${p.name} · ${fmtBytes(p.mem)} · ${Math.round(((p.mem || 0) / max) * 100)}% of the largest`}>
              <span className="fill" style={{ width: `${((p.mem || 0) / max) * 100}%` }}/>
            </span>
          </div>
        ))}
        {rows.length === 0 && <div className="empty tg-empty">{q ? 'Nothing matches that.' : 'No processes reported.'}</div>}
      </div>
    </div>
  );
}
